import React from 'react';
import { Link } from 'react-router-dom';
import { DocumentSheet, DP } from '../../components/shared/folio/DocumentSheet';

/* The privacy policy, drawn as a folio.

   The legal text is carried over word for word from the page this replaces;
   only its wrappers changed, from Heading/BodyText/BulletList to the plain
   prose the sheet styles. The section ids are the original ones, so the
   cross-reference from the GDPR notice still lands where it did. */

const SECTIONS: [string, string][] = [
  ['introduction', "1. Introduction"],
  ['definitions', "2. Definitions"],
  ['data-collection', "3. Data We Collect"],
  ['purposes', "4. Purposes & Legal Basis"],
  ['disclosure', "5. Disclosure"],
  ['processors', "6. Data Processors"],
  ['retention', "7. Data Retention"],
  ['security', "8. Data Security"],
  ['transfers', "9. International Transfers"],
  ['rights', "10. Your Rights"],
  ['breach', "11. Breach Notification"],
  ['cookies', "12. Cookies"],
  ['children', "13. Children's Privacy"],
  ['changes', "14. Changes to Policy"],
  ['governing-law', "15. Governing Law"],
  ['contact', "16. Contact Us"],
];

const PrivacyPolicyPage: React.FC = () => {
  return (
    <DocumentSheet
      title="Privacy Policy"
      code="§ privacy"
      dateline="Last updated: 21 February 2026"
      sections={SECTIONS}
      blockRows={[
        ['Sheet', 'PRIVACY'],
        ['Controller', 'Acanetic Ltd'],
        ['Sections', String(SECTIONS.length)],
      ]}
    >

          {/* 1. Introduction and Data Controller */}
          <section id="introduction">
            <h2>
              1. Introduction and Data Controller
            </h2>
            <p>
              This Privacy Policy (the "<strong>Policy</strong>") is issued by Acanetic Ltd ("<strong>Acanetic</strong>",
              "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>"), a company registered in
              England and Wales. Acanetic operates an online employment marketplace platform (the "<strong>Platform</strong>")
              that connects academic professionals with employment opportunities at universities and research institutions.
            </p>
            <p>
              Acanetic Ltd is the data controller within the meaning of Article 4(7) of Regulation (EU) 2016/679
              (the "<strong>General Data Protection Regulation</strong>" or "<strong>GDPR</strong>") and the UK
              General Data Protection Regulation, and is responsible for determining the purposes and means of
              processing your personal data.
            </p>
            <p>
              This Policy explains how we collect, use, disclose, retain, and protect your personal data when you
              access or use the Platform, and sets out your rights under applicable data protection legislation,
              including the GDPR, the UK GDPR, the Data Protection Act 2018, and the Privacy and Electronic
              Communications Regulations 2003 (as amended). For further detail on our GDPR compliance practices,
              including data subject rights, data processors, and breach notification procedures, please refer
              to our{' '}
              <Link to="/gdpr">
                GDPR Compliance Notice
              </Link>.
              By accessing or using the Platform, you acknowledge that you have read and understood this Policy.
            </p>
          </section>

          {/* 2. Definitions */}
          <section id="definitions">
            <h2>
              2. Definitions
            </h2>
            <p>
              For the purposes of this Policy, the following terms shall have the meanings set out below:
            </p>
            <ul>
                <li><strong>"Personal Data"</strong> means any information relating to an identified or identifiable natural person (a "Data Subject"), as defined in Article 4(1) GDPR.</li>
                <li><strong>"Processing"</strong> means any operation or set of operations performed on Personal Data, whether or not by automated means, as defined in Article 4(2) GDPR.</li>
                <li><strong>"Data Subject"</strong> means an identified or identifiable natural person whose Personal Data is processed by Acanetic.</li>
                <li><strong>"Platform"</strong> means the Acanetic website, web application, and all associated digital services operated by Acanetic Ltd.</li>
                <li><strong>"User"</strong> means any Data Subject who accesses or uses the Platform, including job seekers (employees), employers, and institutional representatives.</li>
                <li><strong>"Processor"</strong> means a natural or legal person which processes Personal Data on behalf of Acanetic as Controller, as defined in Article 4(8) GDPR.</li>
                <li><strong>"Supervisory Authority"</strong> means the Information Commissioner's Office (ICO) in the United Kingdom, or the relevant data protection authority in the EU Member State of the Data Subject's habitual residence.</li>
              
            </ul>
          </section>

          {/* 3. Personal Data We Collect */}
          <section id="data-collection">
            <h2>
              3. Personal Data We Collect
            </h2>
            <p>
              We collect and process Personal Data that you provide directly to us when you create an account,
              complete your profile, apply for positions, communicate with other Users, or contact us for support.
              We may also collect certain data automatically when you interact with the Platform. The categories
              of Personal Data we collect include:
            </p>
            <ul>
                <li><strong>Identity Data:</strong> Full name, email address, telephone number, and other identifying information provided upon registration.</li>
                <li><strong>Professional and Profile Data:</strong> Curriculum vitae (CV), employment history, academic qualifications, skills, education, institutional affiliations, and professional certifications.</li>
                <li><strong>Account Data:</strong> Login credentials (passwords are stored in hashed form only), account preferences, and communication settings.</li>
                <li><strong>Application Data:</strong> Information submitted in connection with job applications, including cover letters, supporting documents, and degree certificates.</li>
                <li><strong>Communication Records:</strong> Messages exchanged through the Platform's messaging system, support enquiries, and correspondence with our team.</li>
                <li><strong>Usage and Technical Data:</strong> Information about how you interact with the Platform, including pages visited, features accessed, session duration, browser type, IP address, and device information.</li>
                <li><strong>Marketing and Communications Data:</strong> Your preferences in respect of receiving communications from us, including opt-in and opt-out records.</li>
              
            </ul>
          </section>

          {/* 4. Purposes of Processing and Legal Basis */}
          <section id="purposes">
            <h2>
              4. Purposes of Processing and Legal Basis
            </h2>
            <p>
              We process your Personal Data for the following purposes, on the corresponding legal basis under
              Article 6(1) GDPR:
            </p>
            <ul>
                <li>
                  <strong>Provision of Platform Services</strong> (Legal basis: Performance of a Contract — Article 6(1)(b)):
                  To create and manage your account, match job seekers with relevant employment opportunities, enable
                  employers to identify and evaluate qualified candidates, facilitate the submission and management of
                  job applications, and deliver the core functionality of the Platform.
                </li>
                <li>
                  <strong>Communications and Notifications</strong> (Legal basis: Performance of a Contract — Article 6(1)(b)):
                  To send you transactional communications including job alerts, application status updates, account
                  verification emails, and password reset notifications.
                </li>
                <li>
                  <strong>Customer Support</strong> (Legal basis: Performance of a Contract — Article 6(1)(b); Legitimate
                  Interests — Article 6(1)(f)):
                  To respond to your enquiries, provide technical assistance, and resolve issues related to your use
                  of the Platform.
                </li>
                <li>
                  <strong>Platform Improvement and Analytics</strong> (Legal basis: Legitimate Interests — Article 6(1)(f)):
                  To analyse usage patterns, monitor Platform performance, and make improvements to the functionality,
                  security, and user experience of the Platform. Our legitimate interest is the continuous improvement
                  and optimisation of our services.
                </li>
                <li>
                  <strong>Legal and Regulatory Compliance</strong> (Legal basis: Legal Obligation — Article 6(1)(c)):
                  To comply with applicable legal obligations, including tax, employment, and anti-fraud legislation,
                  and to respond to lawful requests from public authorities.
                </li>
                <li>
                  <strong>Marketing Communications</strong> (Legal basis: Consent — Article 6(1)(a)):
                  Where you have provided your explicit consent, to send you promotional communications about our
                  services. You may withdraw your consent at any time by contacting us at privacy@acanetic.co.uk.
                </li>
              
            </ul>
          </section>

          {/* 5. Disclosure of Personal Data */}
          <section id="disclosure">
            <h2>
              5. Disclosure of Personal Data
            </h2>
            <p>
              We do not sell, rent, or trade your Personal Data to third parties. We may disclose your Personal Data
              only in the following circumstances:
            </p>
            <ul>
                <li>
                  <strong>With Employers:</strong> When you submit a job application through the Platform, your application
                  materials (including your CV, cover letter, and profile information) are shared with the relevant employer.
                  This disclosure is based on your affirmative action of submitting the application and is necessary for the
                  performance of the contract between you and Acanetic.
                </li>
                <li>
                  <strong>With Service Providers (Data Processors):</strong> We engage trusted third-party Processors to assist
                  in the operation of the Platform, including email delivery, file storage, and database hosting. All Processors
                  are bound by written Data Processing Agreements pursuant to Article 28 GDPR and are permitted to process your
                  Personal Data solely for the purposes specified by us. Full details of our Processors are set out in Section 6
                  of this Policy.
                </li>
                <li>
                  <strong>Legal Requirements:</strong> We may disclose your Personal Data where required to do so by law, regulation,
                  legal process, or enforceable governmental request, or where disclosure is necessary to protect our rights,
                  property, or safety, or the rights, property, or safety of third parties.
                </li>
                <li>
                  <strong>Business Transfers:</strong> In the event of a merger, acquisition, reorganisation, sale of assets,
                  or insolvency, your Personal Data may be transferred to a successor entity or purchaser as part of that
                  transaction. We shall notify you of any such transfer and any changes to the identity of the Controller.
                </li>
              
            </ul>
          </section>

          {/* 6. Data Processors */}
          <section id="processors">
            <h2>
              6. Data Processors
            </h2>
            <p>
              We engage the following third-party service providers as data Processors within the meaning of
              Article 4(8) GDPR. Each Processor is granted access only to the minimum Personal Data necessary
              for the performance of their specific function:
            </p>

            <div className={`${DP}-table-wrap`}>
              <table>
                <thead>
                  <tr>
                    <th>Processor</th>
                    <th>Purpose</th>
                    <th>Personal Data Shared</th>
                    <th>Location of Processing</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Resend</strong></td>
                    <td>Transactional email delivery, including account verification, platform notifications, and password reset communications</td>
                    <td>Email address, first name</td>
                    <td>United States (safeguarded by SCCs)</td>
                  </tr>
                  <tr>
                    <td><strong>Cloudinary</strong></td>
                    <td>Cloud-based file storage and content delivery for user-uploaded documents and images, including CVs, profile photographs, institutional logos, and degree certificates</td>
                    <td>Uploaded files (documents and images)</td>
                    <td>United States / Global CDN (safeguarded by SCCs)</td>
                  </tr>
                  <tr>
                    <td><strong>MongoDB Atlas</strong></td>
                    <td>Primary database hosting for all Platform data, including user accounts, job listings, applications, and messaging</td>
                    <td>All user, job, application, and messaging data</td>
                    <td>EU/UK region</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              All Processors are bound by written Data Processing Agreements ("<strong>DPAs</strong>") entered into
              pursuant to Article 28 GDPR, which impose obligations on the Processor in respect of the security of
              Personal Data, the prohibition of sub-processing without our prior written authorisation, data breach
              notification requirements, and the obligation to delete or return all Personal Data upon termination
              of the processing engagement.
            </p>
          </section>

          {/* 7. Data Retention */}
          <section id="retention">
            <h2>
              7. Data Retention
            </h2>
            <p>
              We retain your Personal Data only for so long as is necessary to fulfil the purposes for which it was
              collected, in accordance with our internal retention policies and applicable legal, accounting, and
              regulatory requirements. The following retention periods apply:
            </p>
            <ul>
                <li><strong>Account Data:</strong> Retained for the duration of your active account and for three (3) years following the last recorded account activity, after which it shall be securely deleted or anonymised.</li>
                <li><strong>Application Data:</strong> Retained for two (2) years from the date of submission, unless a longer retention period is required by law or requested by you.</li>
                <li><strong>Marketing and Communications Data:</strong> Retained until such time as consent is withdrawn, following which your data shall be suppressed from marketing activities without undue delay.</li>
                <li><strong>Usage and Technical Data:</strong> Retained for two (2) years from the date of collection, after which it shall be anonymised or deleted.</li>
              
            </ul>
            <p>
              Upon expiry of the applicable retention period, or upon your valid request for erasure, we shall securely
              delete or irreversibly anonymise your Personal Data in accordance with Article 17 GDPR, unless continued
              retention is required or permitted by applicable law.
            </p>
          </section>

          {/* 8. Data Security */}
          <section id="security">
            <h2>
              8. Data Security
            </h2>
            <p>
              We implement appropriate technical and organisational measures to protect your Personal Data against
              unauthorised access, accidental loss, alteration, disclosure, or destruction, in accordance with
              Article 32 GDPR. These measures include, without limitation:
            </p>
            <ul>
                <li>Encryption of Personal Data in transit (TLS/SSL) and at rest;</li>
                <li>Regular security audits, vulnerability assessments, and penetration testing;</li>
                <li>Role-based access controls and multi-factor authentication mechanisms;</li>
                <li>Ongoing staff training on data protection and information security obligations;</li>
                <li>Regular review and testing of the effectiveness of technical and organisational measures for ensuring the security of the Processing.</li>
              
            </ul>
            <p>
              Whilst we take all reasonable precautions to protect your Personal Data, no method of transmission
              over the Internet or method of electronic storage is entirely secure. We cannot guarantee the absolute
              security of your data, but we are committed to maintaining the highest practicable standards of
              data protection.
            </p>
          </section>

          {/* 9. International Data Transfers */}
          <section id="transfers">
            <h2>
              9. International Data Transfers
            </h2>
            <p>
              In the course of providing our services, we may transfer your Personal Data to countries outside the
              European Economic Area ("<strong>EEA</strong>") and the United Kingdom. Where such transfers occur,
              we ensure that appropriate safeguards are in place in accordance with Chapter V of the GDPR
              (Articles 44–49), including but not limited to:
            </p>
            <ul>
                <li>Transfers to countries that have been the subject of an adequacy decision by the European Commission pursuant to Article 45 GDPR;</li>
                <li>Standard Contractual Clauses ("<strong>SCCs</strong>") as adopted by the European Commission pursuant to Article 46(2)(c) GDPR, supplemented by transfer impact assessments where appropriate;</li>
                <li>Binding Corporate Rules approved pursuant to Article 47 GDPR;</li>
                <li>Approved certification mechanisms pursuant to Article 42 GDPR, together with binding and enforceable commitments of the controller or processor in the third country.</li>
              
            </ul>
            <p>
              As set out in Section 6, certain of our Processors (Resend and Cloudinary) are located in the
              United States. Transfers to these Processors are safeguarded by Standard Contractual Clauses.
              You may request a copy of the safeguards we have put in place in respect of international data
              transfers by contacting us at privacy@acanetic.co.uk.
            </p>
          </section>

          {/* 10. Your Rights Under Data Protection Law */}
          <section id="rights">
            <h2>
              10. Your Rights Under Data Protection Law
            </h2>
            <p>
              Pursuant to Chapter III of the GDPR (Articles 12–23) and applicable UK data protection legislation,
              you are entitled to exercise the following rights in relation to the Personal Data we hold about you:
            </p>
            <ul>
                <li>
                  <strong>Right of Access (Article 15):</strong> You have the right to obtain confirmation as to whether
                  Personal Data concerning you is being processed, and where that is the case, to request access to the
                  Personal Data together with information regarding the purposes of Processing, the categories of data
                  concerned, the recipients, and the envisaged retention period.
                </li>
                <li>
                  <strong>Right to Rectification (Article 16):</strong> You have the right to obtain without undue delay
                  the rectification of inaccurate Personal Data and the completion of incomplete Personal Data.
                </li>
                <li>
                  <strong>Right to Erasure (Article 17):</strong> You have the right to obtain the erasure of Personal Data
                  where the data is no longer necessary for its original purpose, you withdraw consent, you object to
                  Processing and there are no overriding legitimate grounds, or the data has been unlawfully processed.
                  This right is subject to the exceptions set out in Article 17(3) GDPR.
                </li>
                <li>
                  <strong>Right to Restriction of Processing (Article 18):</strong> You have the right to obtain restriction
                  of Processing where you contest the accuracy of the data, the Processing is unlawful, we no longer need
                  the data but you require it for legal claims, or you have objected to Processing pending verification.
                </li>
                <li>
                  <strong>Right to Data Portability (Article 20):</strong> You have the right to receive your Personal Data
                  in a structured, commonly used, and machine-readable format and to transmit it to another controller
                  without hindrance, where Processing is based on consent or contract and carried out by automated means.
                </li>
                <li>
                  <strong>Right to Object (Article 21):</strong> You have the right to object at any time to Processing
                  based on legitimate interests, including profiling. We shall cease Processing unless we demonstrate
                  compelling legitimate grounds that override your interests, rights, and freedoms. Where Personal Data
                  is processed for direct marketing purposes, you have the absolute right to object at any time.
                </li>
                <li>
                  <strong>Right to Withdraw Consent (Article 7(3)):</strong> Where Processing is based on your consent,
                  you have the right to withdraw that consent at any time. Withdrawal shall not affect the lawfulness of
                  Processing based on consent before its withdrawal.
                </li>
                <li>
                  <strong>Rights Related to Automated Decision-Making (Article 22):</strong> You have the right not to be
                  subject to a decision based solely on automated Processing, including profiling, which produces legal
                  effects concerning you or similarly significantly affects you.
                </li>
              
            </ul>

            <h3>
              How to Exercise Your Rights
            </h3>
            <p>
              To exercise any of your rights, please contact us at privacy@acanetic.co.uk. We shall respond to
              your request without undue delay, and in any event within one (1) calendar month of receipt, in
              accordance with Article 12(3) GDPR. Where requests are complex or numerous, this period may be
              extended by a further two (2) months, in which case we shall inform you of the extension and the
              reasons for the delay within one (1) month of receipt.
            </p>
            <p>
              There is no fee for exercising your rights, save where requests are manifestly unfounded or excessive,
              in which case we may charge a reasonable fee or refuse to act on the request, in accordance with
              Article 12(5) GDPR.
            </p>
            <p>
              If you are dissatisfied with our handling of your request, or if you believe that we are processing
              your Personal Data in a manner that is not compliant with applicable data protection law, you have the
              right to lodge a complaint with the relevant Supervisory Authority. In the United Kingdom, the
              Supervisory Authority is the Information Commissioner's Office (ICO), which can be contacted at{' '}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">
                https://ico.org.uk
              </a>, or with the supervisory authority in the EU Member State of your habitual residence.
            </p>
          </section>

          {/* 11. Data Breach Notification */}
          <section id="breach">
            <h2>
              11. Data Breach Notification
            </h2>
            <p>
              In accordance with Articles 33 and 34 GDPR, we maintain documented procedures for the detection,
              reporting, and investigation of Personal Data breaches. In the event of a Personal Data breach:
            </p>
            <ul>
                <li>
                  We shall notify the relevant Supervisory Authority (the ICO) without undue delay and, where feasible,
                  not later than seventy-two (72) hours after becoming aware of the breach, unless the breach is unlikely
                  to result in a risk to the rights and freedoms of natural persons (Article 33(1) GDPR).
                </li>
                <li>
                  Where the breach is likely to result in a high risk to the rights and freedoms of natural persons,
                  we shall communicate the breach to the affected Data Subjects without undue delay (Article 34(1) GDPR),
                  including by direct notification via email.
                </li>
              
            </ul>
            <p>
              Our breach notification shall include the following particulars:
            </p>
            <ul>
                <li>A description of the nature of the Personal Data breach, including, where possible, the categories and approximate number of Data Subjects concerned;</li>
                <li>The name and contact details of the data protection contact from whom further information may be obtained;</li>
                <li>A description of the likely consequences of the breach;</li>
                <li>A description of the measures taken or proposed to be taken to address the breach, including, where appropriate, measures to mitigate its possible adverse effects;</li>
                <li>Recommended steps that affected Data Subjects may take to protect themselves.</li>
              
            </ul>
          </section>

          {/* 12. Cookies and Similar Technologies */}
          <section id="cookies">
            <h2>
              12. Cookies and Similar Technologies
            </h2>
            <p>
              In accordance with the Privacy and Electronic Communications Regulations 2003 (implementing
              Directive 2002/58/EC, the "<strong>ePrivacy Directive</strong>"), we use a single strictly necessary
              cookie for authentication purposes. This cookie is essential for the secure operation of the Platform
              and enables you to remain logged in during your session. As a strictly necessary cookie, it is exempt
              from the requirement to obtain consent under Regulation 6 of the Privacy and Electronic
              Communications Regulations 2003.
            </p>
            <p>
              We do not deploy any third-party tracking, analytics, or marketing cookies on the Platform.
              You may manage your cookie preferences through the cookie consent mechanism provided on the
              Platform or through your browser settings.
            </p>
          </section>

          {/* 13. Children's Privacy */}
          <section id="children">
            <h2>
              13. Children's Privacy
            </h2>
            <p>
              The Platform is not directed at, and is not intended for use by, children under the age of sixteen (16)
              years. We do not knowingly collect or solicit Personal Data from children under the age of sixteen. If
              we become aware that we have collected Personal Data from a child under sixteen without appropriate
              parental or guardian consent, we shall take steps to delete such data without undue delay. If you
              believe that a child under the age of sixteen has provided Personal Data to us, please contact us
              immediately at privacy@acanetic.co.uk.
            </p>
          </section>

          {/* 14. Changes to This Policy */}
          <section id="changes">
            <h2>
              14. Changes to This Policy
            </h2>
            <p>
              We reserve the right to amend or update this Policy at any time to reflect changes in our data
              processing practices, legal requirements, or regulatory guidance. Where we make material changes
              to this Policy, we shall notify you by email to the address associated with your account, or by
              means of a prominent notice on the Platform, prior to the changes taking effect. The "Last updated"
              date at the top of this Policy indicates when the most recent revision was published. Your continued
              use of the Platform following the posting of changes constitutes your acknowledgement of such changes.
            </p>
          </section>

          {/* 15. Governing Law */}
          <section id="governing-law">
            <h2>
              15. Governing Law
            </h2>
            <p>
              This Policy shall be governed by and construed in accordance with the laws of England and Wales.
              Any dispute arising out of or in connection with this Policy shall be subject to the non-exclusive
              jurisdiction of the courts of England and Wales, without prejudice to your right to lodge a complaint
              with your local supervisory authority or to bring proceedings before the courts of your Member State
              of habitual residence in accordance with Article 79 GDPR.
            </p>
          </section>

          {/* 16. Contact Us */}
          <section id="contact">
            <h2>
              16. Contact Us
            </h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data
              protection practices, please contact us:
            </p>
            <div>
              <p>
                <strong>Email:</strong> privacy@acanetic.co.uk<br />
                <strong>Postal Address:</strong> Acanetic Ltd, Cambridge Science Park, Cambridge, CB4 0WZ, United Kingdom<br />
                <strong>Telephone:</strong> +44 20 7123 4567
              </p>
            </div>
          </section>
    </DocumentSheet>
  );
};

export default PrivacyPolicyPage;
